import type { HttpPort } from "~/lib/http";

export interface TrialTerms { trialDays: number; classLimit: number; priceText: string }
export interface RegistrationInput {
  schoolName: string;
  schoolType: string;
  schoolNumber: string;
  street: string;
  postalCode: string;
  city: string;
  estClasses: string;
  firstName: string;
  lastName: string;
  function: string;
  email: string;
  consent: boolean;
  authority: boolean;
  website: string;
}
export interface RegistrationPreview {
  schoolName: string;
  contactName: string;
  contactEmail: string;
  trialDays: number;
  avv: { version: string; text: string };
  released: boolean;
}

export const SCHOOL_TYPES = ["Grundschule", "Hauptschule", "Realschule", "Sekundarschule", "Gesamtschule", "Gymnasium", "Förderschule", "Berufskolleg", "Andere"];
export const CONTACT_FUNCTIONS = ["Schulleitung", "Stellvertretende Schulleitung", "Andere (mit Vollmacht der Schulleitung)"];
export const DEFAULT_TERMS: TrialTerms = { trialDays: 28, classLimit: 3, priceText: "225 € pro Klasse und Schuljahr (zzgl. USt.), bei mehr Klassen günstiger. Bezahlt wird per Rechnung." };

export function createRegistrationApi(http: HttpPort) {
  return {
    terms: () => http.request<TrialTerms>({ path: "/schools/terms" }).catch(() => DEFAULT_TERMS),
    register(input: RegistrationInput) {
      const { estClasses, schoolNumber, schoolType, ...rest } = input;
      return http.request<{ message: string }>({
        path: "/schools/register",
        method: "POST",
        body: { ...rest, ...(schoolType ? { schoolType } : {}), ...(schoolNumber ? { schoolNumber } : {}), ...(estClasses ? { estClasses: Number(estClasses) } : {}) }
      });
    },
    confirm: (token: string) => http.request<{ schoolName: string }>({ path: "/schools/register/confirm", method: "POST", body: { token } }),
    preview: (token: string) => http.request<RegistrationPreview>({ path: "/schools/register/preview", method: "POST", body: { token } })
  };
}

export type RegistrationApi = ReturnType<typeof createRegistrationApi>;

export function readToken(search: string) {
  return new URLSearchParams(search).get("token") ?? "";
}

export type ContractBlock = { kind: "heading"; level: number; text: string } | { kind: "list"; items: string[] } | { kind: "text"; text: string };

// Zerlegt den Vertragstext (Markdown) in einfache Blöcke, die ohne Zusatzbibliothek angezeigt werden.
export function parseContract(markdown: string): ContractBlock[] {
  const blocks: ContractBlock[] = [];
  let list: string[] | null = null;
  const clean = (value: string) => value.replace(/\*\*/g, "").trim();
  const flush = () => {
    if (list) { blocks.push({ kind: "list", items: list }); list = null; }
  };
  for (const raw of markdown.split("\n")) {
    const line = raw.trim();
    if (!line || line === "---") { flush(); continue; }
    const heading = line.match(/^(#{1,3})\s+(.*)$/);
    if (heading) { flush(); blocks.push({ kind: "heading", level: heading[1].length, text: clean(heading[2]) }); continue; }
    if (line.startsWith("- ")) { (list ??= []).push(clean(line.slice(2))); continue; }
    if (line.startsWith("|")) { flush(); const cells = line.split("|").map((cell) => cell.trim()).filter(Boolean); if (!cells.every((cell) => /^-+$/.test(cell))) blocks.push({ kind: "text", text: cells.join(" · ") }); continue; }
    flush();
    blocks.push({ kind: "text", text: clean(line) });
  }
  flush();
  return blocks;
}
